import React from 'react';
import { Card, Group, Avatar, Stack, Text, Badge, Title, ActionIcon, Menu, rem } from '@mantine/core';
import { IconDots, IconMessage, IconDownload, IconCalendar, IconHeart } from '@tabler/icons-react';
import type { Student } from '../../../types/api';

interface ChildProfileHeaderProps {
  child: Student;
  onMessageTeacher?: () => void;
  onDownloadReport?: () => void;
  onViewSchedule?: () => void;
}

export const ChildProfileHeader: React.FC<ChildProfileHeaderProps> = ({ 
  child, 
  onMessageTeacher,
  onDownloadReport,
  onViewSchedule
}) => {
  const getAge = () => {
    if (!child.date_of_birth) return 'Unknown';
    const birthDate = new Date(child.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <Card withBorder p="lg">
      <Group justify="space-between" align="flex-start">
        <Group align="flex-start">
          <Avatar 
            src={child.avatar_url} 
            size="xl" 
            radius="xl"
            color="blue"
          >
            {child.first_name?.[0]}{child.last_name?.[0]}
          </Avatar>
          
          <Stack gap="xs">
            <div>
              <Title order={3}>{child.full_name}</Title>
              <Text size="sm" c="dimmed">
                Student ID: {child.student_id || 'N/A'}
              </Text>
            </div>
            
            <Group gap="xs">
              <Badge color="blue" variant="light">
                Age {getAge()}
              </Badge>
              {child.grade_level && (
                <Badge color="grape" variant="light">
                  Grade {child.grade_level}
                </Badge>
              )}
              {child.class_name && (
                <Badge color="teal" variant="light">
                  {child.class_name}
                </Badge>
              )}
              <Badge color="green" variant="light" leftSection={<IconHeart size={12} />}>
                Present Today
              </Badge>
            </Group>

            {child.teacher && (
              <Text size="sm">
                Teacher: <Text component="span" fw={500}>{child.teacher.full_name}</Text>
              </Text>
            )}
          </Stack>
        </Group>

        <Menu shadow="md" width={200}>
          <Menu.Target>
            <ActionIcon variant="subtle" size="lg">
              <IconDots size={20} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconMessage style={{ width: rem(14), height: rem(14) }} />}
              onClick={onMessageTeacher}
            >
              Message Teacher
            </Menu.Item>
            <Menu.Item
              leftSection={<IconDownload style={{ width: rem(14), height: rem(14) }} />}
              onClick={onDownloadReport}
            >
              Download Report
            </Menu.Item>
            <Menu.Item
              leftSection={<IconCalendar style={{ width: rem(14), height: rem(14) }} />}
              onClick={onViewSchedule}
            >
              View Schedule
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Card>
  );
};

export default ChildProfileHeader;